import { injectable } from 'inversify';
import { FieldProps, FieldRenderProps } from 'react-final-form';

import { ValidateType } from '@vk-hr-tek/app-ui/form/validation';
import { ValidationService } from '@vk-hr-tek/core/validation';

import {
  AvailableDaysCheckValidator,
  CheckValueValidator,
  CompareDateCollectionValidator,
  NumberValidator,
  ValuesCompareValidator,
} from '@app/gen/events';
import { MAX_FILE_SIZE_MB } from '@app/constants';

import { FormAttribute } from '../../types';
import { FormattedValuesCompareValidator } from '../../dto';
import { EventsValidationService } from '../validation';

import { EventsFormAttributesService } from './events.form.attributes.service';

const VALIDATION_MESSAGES = {
  required: 'Обязательное поле',
};

@injectable()
export class EventsAttributeValidatorsService {
  constructor(
    private eventsValidationService: EventsValidationService,
    private eventsFormAttributesService: EventsFormAttributesService,
    private validationService: ValidationService,
  ) {}

  private hasNumericChoices(attribute: FormAttribute) {
    const choices = attribute.options?.choices || [];

    return choices.length > 0 && choices.every((c) => !isNaN(Number(c.value)));
  }

  private getValuesCompareValidators(attribute: FormAttribute) {
    return attribute.validators.filter(
      ({ type }) => type === 'values_compare',
    ) as unknown as FormattedValuesCompareValidator[];
  }

  private isValuesCompareTarget(attribute: FormAttribute) {
    return this.getValuesCompareValidators(attribute).some(
      (validator) => attribute.id === validator.right_attribute_id,
    );
  }

  private isAvailableDaysCheckTarget(attribute: FormAttribute) {
    return attribute.validators.some(
      (validator) =>
        validator.type === 'available_days_check' &&
        'to_attribute_id' in validator &&
        validator.to_attribute_id === attribute.id &&
        'from_attribute_name' in validator &&
        !!validator.from_attribute_name,
    );
  }

  private shouldShowComputableValidatorsList(attribute: FormAttribute) {
    const hasNumberValidator = attribute.validators.some(
      ({ type }) => type === 'number',
    );

    return (
      attribute.validators?.filter((validatorItem) => {
        if (
          validatorItem.type === 'values_compare' ||
          validatorItem.type === 'check_value'
        ) {
          return (
            hasNumberValidator ||
            validatorItem.operation === 'eq' ||
            validatorItem.operation === 'ne'
          );
        }

        return !(
          validatorItem.type === 'non_working_days_included_check' ||
          validatorItem.type === 'exceeding_limit' ||
          hasNumberValidator
        );
      })?.length > 0
    );
  }

  private getChoiceValidateArray({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }): ValidateType[] &
    FieldProps<
      string | undefined,
      FieldRenderProps<string | undefined>
    >['validator'] {
    const { name, nameValidating, options, required, validators } = attribute;

    const hasNumericChoices = this.hasNumericChoices(attribute);

    const valuesCompareValidators = this.getValuesCompareValidators(attribute);

    const checkValueValidator = validators.find(
      ({ type }) => type === 'check_value',
    ) as unknown as CheckValueValidator;

    const valuesCompareValidationMethod = options?.is_multiple
      ? this.eventsValidationService.multiValuesCompareValidator
      : this.eventsValidationService.valuesCompareValidator.bind(
          this.eventsValidationService,
        );

    const validateValuesCompare = valuesCompareValidators
      .filter((validator) => attribute.id === validator.right_attribute_id)
      .map((validator) =>
        valuesCompareValidationMethod({
          leftAttribute: `${fieldIndex}_attribute_${validator.left_attribute_id}`,
          operation: validator.operation,
          message:
            this.eventsValidationService.createCompareValidatorErrorMessage(
              name,
              validator.from_attribute_name || nameValidating || '',
              validator.operation,
            ),
          ...(hasNumericChoices && !options?.is_multiple
            ? { number: true }
            : {}),
        }),
      );

    const validateCheckValue = checkValueValidator
      ? this.eventsValidationService.checkValueValidator({
          message:
            this.eventsValidationService.createCheckValueValidatorErrorMessage(
              name || '',
              checkValueValidator.value,
              checkValueValidator.operation,
            ),
          operation: checkValueValidator.operation,
          valueToCompare: checkValueValidator.value,
        })
      : undefined;

    return [
      required
        ? this.eventsFormAttributesService.getRequiredValidator(
            VALIDATION_MESSAGES.required,
            {
              prefix: fieldIndex,
              hideWhen: options?.hide_when,
              showWhen: options?.show_when,
            },
          )
        : undefined,
      ...validateValuesCompare,
      validateCheckValue,
    ];
  }

  getChoiceAttributeData({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }) {
    const { id, options, relatedAttribute } = attribute;

    return {
      fieldName: `${fieldIndex}_attribute_${id}`,
      relatedAttributeFieldName: `${fieldIndex}_attribute_${relatedAttribute}`,
      choices: options?.choices || [],
      placeholder: options?.placeholder,
      tooltip: options?.tooltip,
      isValuesCompareTarget: this.isValuesCompareTarget(attribute),
      isAvailableDaysCheckTarget: this.isAvailableDaysCheckTarget(attribute),
      validationArray: this.getChoiceValidateArray({ attribute, fieldIndex }),
    };
  }

  getTextAttributeData({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }) {
    const {
      id,
      type,
      name,
      required,
      options,
      description,
      validators,
      nameValidating,
      relatedAttribute,
    } = attribute;

    const fieldName = `${fieldIndex}_attribute_${id}`;
    const relatedAttributeFieldName = `${fieldIndex}_attribute_${relatedAttribute}`;

    const maxLength = options?.max_length;
    const minLength = options?.min_length;

    const numberValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'number',
    ) as unknown as NumberValidator;

    const valuesCompareValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'values_compare',
    ) as unknown as ValuesCompareValidator;

    const checkValueValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'check_value',
    ) as unknown as CheckValueValidator;

    const luhnValidator = validators.find(
      (validatorItem) => validatorItem.type === 'luhn_algorithm',
    );

    const availableDaysCheckValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'available_days_check',
    ) as unknown as AvailableDaysCheckValidator;

    const inputFilter = (value?: string) => {
      if ((luhnValidator || numberValidator) && value) {
        if (availableDaysCheckValidator) {
          return value.replace(/\D/g, '');
        }
        return value.replace(/[^\d.]/gi, '');
      }
      return value;
    };

    const validateArray = [
      required
        ? this.eventsFormAttributesService.getRequiredValidator(
            VALIDATION_MESSAGES.required,
            {
              prefix: fieldIndex,
              hideWhen: options?.hide_when,
              showWhen: options?.show_when,
            },
          )
        : undefined,
      numberValidator
        ? this.validationService.moreOrLess({
            attributeId: id,
            validator: numberValidator,
            field: fieldName,
          })
        : undefined,
      maxLength
        ? this.validationService.maxLength(
            maxLength,
            `Максимальная длина ${maxLength} символов`,
          )
        : undefined,
      valuesCompareValidator && id === valuesCompareValidator.right_attribute_id
        ? this.eventsValidationService.valuesCompareValidator({
            leftAttribute: relatedAttributeFieldName,
            operation: valuesCompareValidator.operation,
            message:
              this.eventsValidationService.createCompareValidatorErrorMessage(
                nameValidating || '',
                name,
                valuesCompareValidator.operation,
              ),
            number: Boolean(numberValidator),
          })
        : undefined,
      type === 'url' ? this.validationService.validUrl() : undefined,
      checkValueValidator
        ? this.eventsValidationService.checkValueValidator({
            message:
              this.eventsValidationService.createCheckValueValidatorErrorMessage(
                name || '',
                checkValueValidator.value,
                checkValueValidator.operation,
              ),
            operation: checkValueValidator.operation,
            valueToCompare: checkValueValidator.value,
            number: Boolean(numberValidator),
          })
        : undefined,
      this.eventsFormAttributesService.getLengthValidator(minLength, maxLength),
      luhnValidator
        ? this.validationService.luhnAlgorithm('Ошибка в номере карты')
        : undefined,
      availableDaysCheckValidator &&
      id === availableDaysCheckValidator.days_attribute_id
        ? this.eventsValidationService.availableDaysCheckValidator({
            vacationTypeAttribute: relatedAttributeFieldName,
            operation: availableDaysCheckValidator.operation,
            availableDaysByType:
              availableDaysCheckValidator.available_days_by_type,
            vacationTypes: availableDaysCheckValidator.vacation_types,
          })
        : undefined,
    ];

    return {
      fieldName,
      inputFilter,
      placeholder: availableDaysCheckValidator
        ? options?.placeholder
        : options?.placeholder || description,
      helperText: availableDaysCheckValidator ? description : undefined,
      validateArray,
    };
  }

  getCatalogAttributeData({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }) {
    const {
      id,
      name,
      required,
      options,
      validators,
      nameValidating,
      relatedAttribute,
    } = attribute;

    const valuesCompareValidator = validators.find(
      ({ type }) => type === 'values_compare',
    ) as unknown as ValuesCompareValidator;

    const checkValueValidator = validators.find(
      ({ type }) => type === 'check_value',
    ) as unknown as CheckValueValidator;

    const valuesCompareValidationMethod = options?.is_multiple
      ? this.eventsValidationService.multiValuesCompareValidator
      : this.eventsValidationService.valuesCompareValidator.bind(
          this.eventsValidationService,
        );

    const validateArray: ValidateType[] &
      FieldProps<
        string | undefined,
        FieldRenderProps<string | undefined>
      >['validator'] = [
      required
        ? this.eventsFormAttributesService.getRequiredValidator(
            VALIDATION_MESSAGES.required,
            {
              prefix: fieldIndex,
              hideWhen: options?.hide_when,
              showWhen: options?.show_when,
            },
          )
        : undefined,
      valuesCompareValidator && id === valuesCompareValidator.right_attribute_id
        ? valuesCompareValidationMethod({
            leftAttribute: `${fieldIndex}_attribute_${relatedAttribute}`,
            operation: valuesCompareValidator.operation,
            message:
              this.eventsValidationService.createCompareValidatorErrorMessage(
                nameValidating || '',
                name,
                valuesCompareValidator.operation,
              ),
          })
        : undefined,
      checkValueValidator
        ? this.eventsValidationService.checkValueValidator({
            message:
              this.eventsValidationService.createCheckValueValidatorErrorMessage(
                name || '',
                checkValueValidator.value,
                checkValueValidator.operation,
              ),
            operation: checkValueValidator.operation,
            valueToCompare: checkValueValidator.value,
          })
        : undefined,
    ];

    return {
      fieldName: `${fieldIndex}_attribute_${id}`,
      validateArray,
    };
  }

  getFileValidateArray({
    attribute,
    fieldIndex,
    fileTypes,
    formatMessage,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
    fileTypes: string[];
    formatMessage: string;
  }) {
    const { id, options, required } = attribute;

    const fieldName = `${fieldIndex}_attribute_${id}`;

    const validatorRequiredIfFile = this.validationService.requiredIfFile(
      formatMessage,
      {
        field: fieldName,
        fileTypes,
        maxSize: MAX_FILE_SIZE_MB,
      },
    );

    const validatorSizeLimit = this.validationService.sizeLimit(formatMessage, {
      field: fieldName,
      sizeLimit: MAX_FILE_SIZE_MB,
    });

    const validatorRequired =
      this.eventsFormAttributesService.getRequiredValidator(
        VALIDATION_MESSAGES.required,
        {
          prefix: fieldIndex,
          hideWhen: options?.hide_when,
          showWhen: options?.show_when,
        },
      );

    return required
      ? [validatorRequired, validatorSizeLimit, validatorRequiredIfFile]
      : [validatorSizeLimit, validatorRequiredIfFile];
  }

  getComputableAttributeData({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }) {
    const {
      id,
      name,
      required,
      options,
      relatedAttribute,
      validators,
      nameValidating,
    } = attribute;

    const valuesCompareValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'values_compare',
    ) as unknown as ValuesCompareValidator;

    const checkValueValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'check_value',
    ) as unknown as CheckValueValidator;

    const numberValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'number',
    ) as unknown as NumberValidator;

    const compareDateCollectionsValidator = validators.find(
      ({ type: validatorType }) => validatorType === 'compare_date_collection',
    ) as unknown as CompareDateCollectionValidator;

    const validateArray: (ValidateType | undefined)[] = [
      required
        ? this.eventsFormAttributesService.getRequiredValidator(
            VALIDATION_MESSAGES.required,
            {
              prefix: fieldIndex,
              hideWhen: options?.hide_when,
              showWhen: options?.show_when,
            },
          )
        : undefined,
      valuesCompareValidator && id === valuesCompareValidator.right_attribute_id
        ? this.eventsValidationService.valuesCompareValidator({
            leftAttribute: `${fieldIndex}_attribute_${relatedAttribute}`,
            operation: valuesCompareValidator.operation,
            message:
              this.eventsValidationService.createCompareValidatorErrorMessage(
                nameValidating || '',
                name,
                valuesCompareValidator.operation,
              ),
            number: Boolean(numberValidator),
          })
        : undefined,
      checkValueValidator
        ? this.eventsValidationService.checkValueValidator({
            message:
              this.eventsValidationService.createCheckValueValidatorErrorMessage(
                name || '',
                checkValueValidator.value,
                checkValueValidator.operation,
              ),
            operation: checkValueValidator.operation,
            valueToCompare: checkValueValidator.value,
            number: Boolean(numberValidator),
          })
        : undefined,
      compareDateCollectionsValidator && fieldIndex !== ''
        ? this.eventsValidationService.compareDateCollectionsValidatorMessage(
            compareDateCollectionsValidator,
            fieldIndex,
          )
        : undefined,
    ];

    return {
      validateArray,
    };
  }

  getFixedCatalogValidateArray({
    attribute,
    fieldIndex,
  }: {
    attribute: FormAttribute;
    fieldIndex: string;
  }): ValidateType[] &
    FieldProps<
      string | undefined,
      FieldRenderProps<string | undefined>
    >['validator'] {
    const {
      id,
      name,
      nameValidating,
      options,
      relatedAttribute,
      required,
      validators,
    } = attribute;

    const valuesCompareValidator = validators.find(
      ({ type }) => type === 'values_compare',
    ) as unknown as ValuesCompareValidator;

    return [
      required
        ? this.eventsFormAttributesService.getRequiredValidator(
            VALIDATION_MESSAGES.required,
            {
              prefix: fieldIndex,
              hideWhen: options?.hide_when,
              showWhen: options?.show_when,
            },
          )
        : undefined,

      valuesCompareValidator && id === valuesCompareValidator.right_attribute_id
        ? this.eventsValidationService.multiValuesCompareValidator({
            leftAttribute: `${fieldIndex}_attribute_${relatedAttribute}`,
            operation: valuesCompareValidator.operation,
            message:
              this.eventsValidationService.createCompareValidatorErrorMessage(
                nameValidating || '',
                name,
                valuesCompareValidator.operation,
              ),
          })
        : undefined,
    ];
  }
}
